import { take } from 'rxjs';
import SignaturePad from 'signature_pad';

import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ScheduledAssessment } from '@leads/shared/models/domain/scheduled-assessment.domain';
import { LeadStorageService } from '@leads/shared/services/lead-storage/lead-storage.service';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ModalService } from '@shared/services/modal/modal.service';

@Component({
	selector: 'app-job-card-signature',
	imports: [CommonModule, LoadingStateComponent],
	templateUrl: './job-card-signature.component.html',
	styleUrl: './job-card-signature.component.scss',
})
export class JobCardSignatureComponent implements OnInit {
	@Input() scheduledAssessment: ScheduledAssessment;
	@Input() scheduledAssessmentId: string;
	@Output() signatureSaved = new EventEmitter<Blob>();

	private leadStorage = inject(LeadStorageService);
	private modalService = inject(ModalService);

	customerSignatureUrl: string | null;
	isLoading = true;
	isImgLoaded = false;

	@ViewChild('signatureCanvas') signatureCanvas!: ElementRef<HTMLCanvasElement>;
	signaturePad!: SignaturePad;

	ngOnInit(): void {
		this.leadStorage
			.getCustomerAssessmentSignatureUrl(
				this.scheduledAssessment.organisationId,
				this.scheduledAssessment.leadRequestId,
				this.scheduledAssessmentId,
			)
			.then((url) => {
				this.customerSignatureUrl = url;
				this.isLoading = false;
			});
	}

	initSignaturePad() {
		setTimeout(() => {
			const canvas = this.signatureCanvas?.nativeElement;
			if (!canvas) {
				return;
			}
			this.signaturePad = new SignaturePad(canvas, {
				minWidth: 1,
				maxWidth: 2.5,
				penColor: 'black',
				backgroundColor: 'rgba(255, 255, 255, 0)',
			});

			this.resizeCanvas();
		});
	}

	private resizeCanvas() {
		const canvas = this.signatureCanvas.nativeElement;
		const ratio = Math.max(window.devicePixelRatio || 1, 1);
		canvas.width = canvas.offsetWidth * ratio;
		canvas.height = canvas.offsetHeight * ratio;
		canvas.getContext('2d')?.scale(ratio, ratio);

		this.signaturePad.clear();
	}

	clearSignature() {
		this.signaturePad.clear();
	}

	async saveSignature() {
		const modalRef = this.modalService.open(ConfirmationModalComponent, false, 'md');
		const componentInstance = modalRef.componentInstance as ConfirmationModalComponent;
		componentInstance.title = 'Are you sure you want to continue?';
		componentInstance.contentText = 'This cannot be edited once confirmed';
		componentInstance.confirmButtonColur = 'btn-primary';
		componentInstance.backButtonColour = 'btn-outline-primary';
		componentInstance.confirmAction.pipe(take(1)).subscribe(() => {
			const dataURL = this.signaturePad.toDataURL('image/png');
			const blob = this.dataURLToBlob(dataURL);

			this.signatureSaved.emit(blob);
		});
	}

	private dataURLToBlob(dataURL: string): Blob {
		const parts = dataURL.split(',');
		const byteString = atob(parts[1]);
		const mimeString = parts[0].split(':')[1].split(';')[0];

		const byteArray = new Uint8Array(byteString.length);
		for (let i = 0; i < byteString.length; i++) {
			byteArray[i] = byteString.charCodeAt(i);
		}

		return new Blob([byteArray], { type: mimeString });
	}
}
